import { useMemo } from "react"
import { useInfiniteQuery } from "react-query"
import ProofOfOwnershipService from "~services/ProofOfOwnershipService"
import { AssignedQueueFilter, ProofOfOwnership } from "~shapes/proofOfOwnership"

const PAGE_SIZE = 25

interface PooPage {
  data: ProofOfOwnership[]
  next?: string
}

// The operator validation queue (GET .../assigned): the POOs the caller — or, for
// elevated callers, the inspected assignee — currently has claimed (VALIDATING).
// Mirrors useProofsOfOwnership's cursor pagination + de-dup so the create-portfolio
// page can swap data sources without changing how it consumes the list.
const useAssignedProofsOfOwnership = (
  filter: AssignedQueueFilter = {},
  enabled = true,
  sort?: string,
  refetchOnMount: boolean | "always" = true,
) => {
  const { assigneeId, assigneeType } = filter

  const query = useInfiniteQuery<PooPage>(
    [
      "assigned-proofs-of-ownership",
      assigneeType ?? "self",
      assigneeId ?? "",
      sort ?? "",
    ],
    ({ pageParam }) =>
      ProofOfOwnershipService.getAssignedProofsOfOwnership(
        filter,
        PAGE_SIZE,
        pageParam,
        sort,
      ),
    {
      enabled,
      staleTime: 2 * 60 * 1000,
      refetchOnMount,
      getNextPageParam: lastPage => lastPage.next || undefined,
    },
  )

  const pages = query.data?.pages
  const data = useMemo(() => {
    const allItems = pages?.flatMap(p => p.data) ?? []
    const seen = new Set<string>()
    return allItems.filter(item => {
      if (seen.has(item.id)) return false
      seen.add(item.id)
      return true
    })
  }, [pages])

  return {
    isFetching: query.isFetching,
    data,
    refetch: query.refetch,
    isError: query.isError,
    fetchNextPage: query.fetchNextPage,
    hasNextPage: query.hasNextPage ?? false,
    isFetchingNextPage: query.isFetchingNextPage,
  }
}

export default useAssignedProofsOfOwnership
